/* global window */

import * as React from 'react';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import type {CSSProperties} from '@packages/utilities/css-properties-type';
import type {StyleAttributeImm} from '@packages/systems/wfdl';
import {getActiveDocument} from '@packages/systems/core/helpers/SiteIframeMigrationHelper';
import {useRuntimeEnvironment} from '@packages/systems/render';

type BackgroundVideoVideoProps = {
  autoPlay?: string | StyleAttributeImm;
  loop?: string | StyleAttributeImm;
  previewMode: boolean;
  videoStyle?: CSSProperties | null;
  videoURLs: string[];
  id: string;
};

export default function BackgroundVideoVideo({
  autoPlay,
  loop,
  previewMode,
  videoStyle,
  videoURLs,
  id,
}: BackgroundVideoVideoProps) {
  React.useLayoutEffect(() => {
    if (previewMode) {
      // setTimeout to avoid race condition with play/pause
      setTimeout(function () {
        // playing & pausing videos when this MQ changes is handled by webflow-bgvideo.js
        // but the initial check in that file runs before the preview mode is enabled, so
        // we check it here when turning preview mode on
        const {matches: prefersReducedMotion} = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        );

        // @ts-expect-error - TS2531 - Object is possibly 'null'.
        const previewVideo = getActiveDocument().querySelector(
          `video[id="${id}"]`
        );
        // @ts-expect-error - TS18047 - 'previewVideo' is possibly 'null'. | TS18047 - 'previewVideo.parentElement' is possibly 'null'.
        const playPauseButton = previewVideo.parentElement.querySelector(
          '[data-w-bg-video-control]'
        );

        if (playPauseButton && (prefersReducedMotion || !autoPlay)) {
          // @ts-expect-error - TS18047 - 'previewVideo' is possibly 'null'. | TS2339 - Property 'pause' does not exist on type 'Element'.
          previewVideo.pause();

          // toggle icons
          [...playPauseButton.children].forEach(
            (child, i) =>
              i === 0
                ? child.setAttribute('hidden', '') // hide the pause icon
                : child.removeAttribute('hidden') // show the play icon
          );
        }
      }, 150);
    }
  }, [previewMode, autoPlay, id]);

  const runtimeEnvironment = useRuntimeEnvironment();
  const shouldAutoPlay =
    autoPlay &&
    (runtimeEnvironment !== WebflowEnvironments.DESIGNER || previewMode);

  const videoKey = React.useMemo(() => videoURLs.join(','), [videoURLs]);
  return (
    <video
      key={videoKey}
      id={id}
      // @ts-expect-error - TS2322 - Type 'true | null' is not assignable to type 'boolean | undefined'.
      autoPlay={shouldAutoPlay || null}
      // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'boolean | undefined'.
      loop={loop ? 'loop' : null}
      // @ts-expect-error - TS2322 - Type 'ObjMap<StrictCSSProperties, ToMaybeType> | null | undefined' is not assignable to type 'CSSProperties | undefined'.
      style={videoStyle}
      muted
      playsInline
      data-wf-ignore
      data-object-fit="cover"
    >
      {videoURLs.map((url) => {
        return <source src={url} key={url} data-wf-ignore />;
      })}
    </video>
  );
}
